import {
  EWFArrowType,
  EWFAssignmentType,
  EWFDueUnit,
  EWFEventTag,
  EWFEventType,
  EWFGatewayType,
  EWFNodeType,
  EWFStepType,
  IWFBoundaryEvent,
  IWFDecision,
  IWFDue,
  IWFEvent,
  IWFForward,
  IWFGateway,
  IWFLane,
  IWFNodeAssignment,
  IWFNodes,
  IWFStep,
  IWorkFlow
} from "@kuika/kuika-cl-model";
import { ModdleElement } from "bpmn-js/lib/BaseViewer";

export interface SyncInconsistency {
  elementId: string;
  type: string;
  message: string;
}

export class WorkflowSyncService {
  /**
   * Syncs a BPMN element with the workflow model
   */
  syncElement(element: ModdleElement, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    if (!element || !workflow) return;

    const businessObject = element.businessObject;
    if (!businessObject) return;

    switch (businessObject.$type) {
      case "bpmn:Task":
      case "bpmn:UserTask":
      case "bpmn:SendTask":
        this.syncTask(businessObject, workflow, currentTargetWorkflow);
        break;
      case "bpmn:SubProcess":
        this.syncSubprocess(businessObject, workflow, currentTargetWorkflow);
        break;
      case "bpmn:CallActivity":
        this.syncLinkedProcess(businessObject, workflow, currentTargetWorkflow);
        break;
      case "bpmn:ExclusiveGateway":
      case "bpmn:ParallelGateway":
      case "bpmn:InclusiveGateway":
        this.syncGateway(businessObject, workflow, currentTargetWorkflow);
        break;
      case "bpmn:StartEvent":
      case "bpmn:EndEvent":
      case "bpmn:BoundaryEvent":
      case "bpmn:IntermediateCatchEvent":
        this.syncEvent(businessObject, workflow, currentTargetWorkflow);
        break;
      case "bpmn:SequenceFlow":
        this.syncSequenceFlow(businessObject, workflow, currentTargetWorkflow);
        break;
      case "bpmn:Lane":
        this.syncLane(businessObject, workflow, currentTargetWorkflow);
        break;
    }
  }

  getNodeType(element: any): EWFNodeType | undefined {
    if (!element) return undefined;

    const elementType = element.type;
    if (elementType === "bpmn:Task" || elementType === "bpmn:UserTask") {
      return EWFNodeType.Step;
    }
    if (
      elementType === "bpmn:StartEvent" ||
      elementType === "bpmn:EndEvent" ||
      elementType === "bpmn:BoundaryEvent" ||
      elementType === "bpmn:IntermediateCatchEvent"
    ) {
      return EWFNodeType.Event;
    }
    if (
      elementType === "bpmn:ExclusiveGateway" ||
      elementType === "bpmn:ParallelGateway" ||
      elementType === "bpmn:InclusiveGateway"
    ) {
      return EWFNodeType.Gateway;
    }
    if (elementType === "bpmn:SubProcess" || elementType === "bpmn:CallActivity") {
      return EWFNodeType.Step; // SubProcess & LinkedProcess are handled as a Step type
    }
    if (elementType === "bpmn:Lane") {
      return EWFNodeType.Lane;
    }
    return undefined;
  }

  /**
   * Gets the node ID from a BPMN element
   */
  getNodeId(element: any): string {
    if (!element || !element.businessObject || !element.id) return "";
    return element.id;
  }

  /**
   * Gets the node name from a BPMN element
   */
  getNodeName(element: any): string {
    if (!element || !element.businessObject || !element.businessObject.name) return "";
    return element.businessObject.name;
  }

  /**
   * Gets the step type from a BPMN element
   */
  getStepType(element: any): EWFStepType | undefined {
    if (!element || !element.businessObject || !element.id || !element.businessObject.$type) {
      return undefined;
    }

    const stepTypeStr: string = element.businessObject.$type.replace("bpmn:", "");
    if (stepTypeStr === "UserTask") return EWFStepType.ScreenInteraction;
    if (stepTypeStr === "Task") return EWFStepType.BackendInteraction;
    return undefined;
  }

  /**
   * Checks if two DOM elements overlap
   */
  elementsOverlap(el1: Element | null, el2: Element | null): boolean {
    if (!el1 || !el2) return false;

    const domRect1 = el1.getBoundingClientRect();
    const domRect2 = el2.getBoundingClientRect();
    const el2center = { x: domRect2.x + domRect2.width / 2, y: domRect2.y + domRect2.height / 2 };

    return (
      domRect1.left < el2center.x &&
      domRect1.right > el2center.x &&
      domRect1.top < el2center.y &&
      domRect1.bottom > el2center.y
    );
  }

  private syncTask(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow?.steps) return;

    let step = targetWorkflow.steps.find((s) => s.id === element.id);
    const stepType = element.$type === "bpmn:UserTask" ? EWFStepType.ScreenInteraction : EWFStepType.BackendInteraction;

    if (!step) {
      // Create new step
      step = {
        id: element.id,
        name: element.name,
        description: "",
        stepType,
        nodeType: EWFNodeType.Step,
        useLaneAssignment: true,
        assignment: {
          assignmentId: "Everyone",
          assignmentType: EWFAssignmentType.everyone,
          dynamicAssignmentAction: undefined,
          roleAssignmentParameter: undefined
        } as IWFNodeAssignment,
        inputParameters: [],
        outputParameters: [],
        parameters: [],
        dueIn: {
          dueUnit: EWFDueUnit.Days,
          dueValue: 1
        } as IWFDue
      } as IWFStep;
      targetWorkflow.steps.push(step);
    } else {
      // TODO: Update existing step. there must be other properties to follow
      step.name = element.name;
      step.stepType = stepType;
    }
    if (step.stepType === EWFStepType.BackendInteraction) {
      step.assignment = {
        assignmentId: "None",
        assignmentType: EWFAssignmentType.none,
        dynamicAssignmentAction: undefined,
        roleAssignmentParameter: undefined
      };
    }

    // Handle lane assignment
    else if (step.useLaneAssignment) {
      const stepElement = document.querySelector(`[data-element-id=${step.id}]`);
      if (!stepElement) return;

      // First try to find a lane in the current process
      let assigned = false;
      for (const lane of targetWorkflow.lanes ?? []) {
        const laneElement = document.querySelector(`[data-element-id=${lane.id}]`);
        if (this.elementsOverlap(laneElement, stepElement)) {
          step.assignment = { ...lane.assignment };
          assigned = true;
          break;
        }
      }

      // If no lane found and we're in a subprocess, try parent process lanes
      if (!assigned && this.findParentSubprocess(element)) {
        const parentProcess = workflow.nodes;
        for (const lane of parentProcess?.lanes ?? []) {
          const laneElement = document.querySelector(`[data-element-id=${lane.id}]`);
          if (this.elementsOverlap(laneElement, stepElement)) {
            step.assignment = { ...lane.assignment };
            break;
          }
        }
      }
    }
  }

  private syncSubprocess(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow) return;

    let subprocessStep = targetWorkflow.steps.find((s) => s.id === element.id);
    if (!subprocessStep) {
      subprocessStep = {
        id: element.id,
        name: element.name,
        nodeType: EWFNodeType.Step,
        stepType: EWFStepType.Subprocess,
        description: "",
        dueIn: {
          dueUnit: EWFDueUnit.Days,
          dueValue: 0
        } as IWFDue,
        parameters: [],
        inputParameters: [],
        outputParameters: [],
        useLaneAssignment: false,
        assignment: {
          assignmentId: "Everyone",
          assignmentType: EWFAssignmentType.everyone,
          dynamicAssignmentAction: undefined,
          roleAssignmentParameter: undefined
        } as IWFNodeAssignment,
        subprocess: {
          description: "",
          dueIn: {
            dueUnit: EWFDueUnit.Days,
            dueValue: 0
          } as IWFDue,
          steps: [],
          forwards: [],
          decisions: [],
          gateways: [],
          events: [],
          lanes: []
        } as IWFNodes
      } as IWFStep;
      targetWorkflow.steps.push(subprocessStep);
    } else {
      subprocessStep.name = element.name;
    }

    // Handle lane assignment for subprocess
    if (subprocessStep.useLaneAssignment) {
      const subprocessElement = document.querySelector(`[data-element-id=${subprocessStep.id}]`);
      if (!subprocessElement) return;

      // First try to find a lane in the current process
      let assigned = false;
      for (const lane of targetWorkflow.lanes ?? []) {
        const laneElement = document.querySelector(`[data-element-id=${lane.id}]`);
        if (this.elementsOverlap(laneElement, subprocessElement)) {
          subprocessStep.assignment = { ...lane.assignment };
          assigned = true;
          break;
        }
      }

      // If no lane found and we're in a subprocess, try parent process lanes
      if (!assigned && this.findParentSubprocess(element)) {
        const parentProcess = workflow.nodes;
        for (const lane of parentProcess?.lanes ?? []) {
          const laneElement = document.querySelector(`[data-element-id=${lane.id}]`);
          if (this.elementsOverlap(laneElement, subprocessElement)) {
            subprocessStep.assignment = { ...lane.assignment };
            break;
          }
        }
      }
    }

    // Process elements inside the subprocess
    if (element.flowElements && subprocessStep.subprocess) {
      // First sync lanes to ensure they exist for task assignments
      const lanes = element.flowElements.filter((e) => e.$type === "bpmn:Lane");
      lanes.forEach((lane) => {
        this.syncLane(lane, workflow, subprocessStep.subprocess);
      });

      // Then sync all other elements
      element.flowElements.forEach((childElement) => {
        if (childElement.$type !== "bpmn:Lane") {
          this.syncElement(childElement, workflow, subprocessStep.subprocess);
        }
      });
    }
  }

  private syncLinkedProcess(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow) return;

    let callActivity = targetWorkflow.steps.find((s) => s.id === element.id);
    if (!callActivity) {
      callActivity = {
        id: element.id,
        name: element.name,
        nodeType: EWFNodeType.Step,
        stepType: EWFStepType.LinkedProcess,
        description: "",
        actions: [],
        dueIn: {
          dueUnit: EWFDueUnit.Days,
          dueValue: 1
        } as IWFDue,
        parameters: [],
        inputParameters: [],
        outputParameters: [],
        useLaneAssignment: false,
        linkedProcess: {
          linkedProcessId: "",
          linkedProcessMappings: []
        },
        assignment: {
          assignmentId: "Everyone",
          assignmentType: EWFAssignmentType.everyone,
          dynamicAssignmentAction: undefined,
          roleAssignmentParameter: undefined
        } as IWFNodeAssignment
      };
      targetWorkflow.steps.push(callActivity);
    } else {
      callActivity.name = element.name;
    }
  }

  private syncGateway(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow?.gateways) return;
    const gatewayType =
      element.$type === "bpmn:ExclusiveGateway"
        ? EWFGatewayType.Conditional
        : element.$type === "bpmn:InclusiveGateway"
          ? EWFGatewayType.Inclusive
          : EWFGatewayType.Paralel;

    let gateway = targetWorkflow.gateways.find((g) => g.id === element.id);
    if (!gateway) {
      gateway = {
        id: element.id,
        name: element.name,
        nodeType: EWFNodeType.Gateway,
        gateWayType: gatewayType
      } as IWFGateway;
      targetWorkflow.gateways.push(gateway);
    } else {
      gateway.name = element.name;
      gateway.gateWayType = gatewayType;
    }
  }

  private syncEvent(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow?.events) return;

    const eventType =
      element.$type === "bpmn:StartEvent"
        ? EWFEventType.Start
        : element.$type === "bpmn:EndEvent"
          ? EWFEventType.End
          : element.$type === "bpmn:IntermediateCatchEvent"
            ? EWFEventType.Intermediate
            : EWFEventType.Boundary;

    switch (eventType) {
      case EWFEventType.Start:
      case EWFEventType.End:
        let event = targetWorkflow.events.find((e) => e.id === element.id);
        if (!event) {
          event = {
            id: element.id,
            name: element.name ?? (eventType === EWFEventType.Start ? "START" : "END"),
            nodeType: EWFNodeType.Event,
            eventType
          } as IWFEvent;
          targetWorkflow.events.push(event);
        } else {
          event.name = element.name;
        }
        break;
      case EWFEventType.Intermediate:
        // TODO: add Intermediate event handling here
        let intermediateEvent = targetWorkflow.events.find((e) => e.id === element.id);
        if (!intermediateEvent) {
          intermediateEvent = {
            id: element.id,
            name: element.name ?? "Intermediate Event",
            nodeType: EWFNodeType.Event,
            eventType: EWFEventType.Intermediate,
            eventTag: this.getEventTag(element),
            dueIn: {
              dueUnit: EWFDueUnit.Days,
              dueValue: 1
            }
          } as IWFEvent;
          targetWorkflow.events.push(intermediateEvent);
        } else {
          intermediateEvent.name = element.name;
        }
        break;
      case EWFEventType.Boundary:
        const attachedStepId = element.attachedToRef?.id;
        const attachedStep = targetWorkflow.steps?.find((s) => s.id === attachedStepId);

        if (attachedStepId && attachedStep) {
          const boundaryEvent: IWFBoundaryEvent = {
            id: element.id,
            name: element.name ?? "Boundary Event",
            nodeType: EWFNodeType.Event,
            eventType: EWFEventType.Boundary,
            eventTag: this.getEventTag(element),
            isInterrupting: element.cancelActivity !== false, // Default to true if not specified
            timerEventDue: {
              dueUnit: EWFDueUnit.Days,
              dueValue: 1
            },
            useStepTimer: false
          };
          attachedStep.boundaryEvent = boundaryEvent;
          console.log(`[WorkflowSync] Linked boundary event ${element.id} to step ${attachedStepId}`);
        } else {
          console.warn(
            `[WorkflowSync] Could not find attached step ${attachedStepId} for boundary event ${element.id}`
          );
        }
        break;
    }

    // if (eventType !== EWFEventType.Boundary) {
    //   let event = targetWorkflow.events.find((e) => e.id === element.id);
    //   if (!event) {
    //     event = {
    //       id: element.id,
    //       name: element.name ?? (eventType === EWFEventType.Start ? "START" : "END"),
    //       nodeType: EWFNodeType.Event,
    //       eventType
    //     } as IWFEvent;
    //     targetWorkflow.events.push(event);
    //   } else {
    //     event.name = element.name;
    //   }
    // } else if (eventType === EWFEventType.Boundary && element.attachedToRef) {
    //   const attachedStepId = element.attachedToRef.id;
    //   const attachedStep = targetWorkflow.steps?.find((s) => s.id === attachedStepId);

    //   if (attachedStep) {
    //     const boundaryEvent: IWFBoundaryEvent = {
    //       id: element.id,
    //       name: element.name,
    //       nodeType: EWFNodeType.Event,
    //       eventType: EWFEventType.Boundary,
    //       eventTag: this.getEventTag(element),
    //       isInterrupting: element.cancelActivity !== false, // Default to true if not specified
    //       timerEventDue: element.timerEventDefinition
    //         ? {
    //             dueUnit: EWFDueUnit.Days,
    //             dueValue: 1
    //           }
    //         : undefined,
    //       useStepTimer: false
    //     };

    //     attachedStep.boundaryEvent = boundaryEvent;

    //     console.log(`[WorkflowSync] Linked boundary event ${element.id} to step ${attachedStepId}`);
    //   } else {
    //     console.warn(`[WorkflowSync] Could not find attached step ${attachedStepId} for boundary event ${element.id}`);
    //   }
    // }
  }

  private getEventTag(element: any): EWFEventTag {
    var elementEventDefinition = element.eventDefinitions?.[0];
    if (elementEventDefinition) {
      if (elementEventDefinition.$type === "bpmn:TimerEventDefinition") return EWFEventTag.Timer;
      if (elementEventDefinition.$type === "bpmn:MessageEventDefinition") return EWFEventTag.Message;
      if (elementEventDefinition.$type === "bpmn:SignalEventDefinition") return EWFEventTag.Signal;
    }
    return EWFEventTag.Undefined;
  }

  private syncSequenceFlow(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow) return;

    targetWorkflow.forwards = targetWorkflow.forwards || [];
    targetWorkflow.decisions = targetWorkflow.decisions || [];

    const forwards = targetWorkflow.forwards;
    const decisions = targetWorkflow.decisions;

    const src = element.sourceRef;
    if (!src) return;

    const srcType = src.$type;
    const isExclusive = srcType === "bpmn:ExclusiveGateway";
    const isInclusive = srcType === "bpmn:InclusiveGateway";
    const isDiverging = (src.outgoing?.length ?? 0) > (src.incoming?.length ?? 0);
    const isDecision = isExclusive || (isInclusive && isDiverging);

    if (isDecision) {
      const outgoing = src.outgoing || [];

      let isDefault: boolean;
      if (isInclusive) {
        isDefault = !!src.default && element.id === src.default.id;
      } else {
        isDefault = outgoing.length < 2 || (src.default && element.id === src.default.id);
      }

      const forwardsFromSrc = forwards.filter((f) => f.fromNodeId === src.id);
      if (forwardsFromSrc.length > 0) {
        forwardsFromSrc.forEach((f) => {
          let existing = decisions.find((d) => d.id === f.id);
          if (!existing) {
            const upgraded: IWFDecision = {
              id: f.id,
              name: f.name,
              nodeType: EWFNodeType.Arrow,
              arrowType: EWFArrowType.Decision,
              fromNodeId: f.fromNodeId,
              toNodeId: f.toNodeId,
              isDefault: false
            } as IWFDecision;
            decisions.push(upgraded);
          } else {
            existing.arrowType = EWFArrowType.Decision;
            existing.fromNodeId = f.fromNodeId;
            existing.toNodeId = f.toNodeId;
          }
        });

        targetWorkflow.forwards = forwards.filter((f) => f.fromNodeId !== src.id);
      }

      let decision = decisions.find((d) => d.id === element.id);
      if (!decision) {
        decision = {
          id: element.id,
          name: element.name,
          nodeType: EWFNodeType.Arrow,
          arrowType: EWFArrowType.Decision,
          fromNodeId: src.id,
          toNodeId: element.targetRef.id,
          isDefault
        } as IWFDecision;
        decisions.push(decision);
      } else {
        decision.name = element.name;
        decision.fromNodeId = src.id;
        decision.toNodeId = element.targetRef.id;
        decision.isDefault = isDefault;
        decision.arrowType = EWFArrowType.Decision;
      }

      targetWorkflow.forwards = targetWorkflow.forwards.filter((f) => f.fromNodeId !== src.id);
    } else {
      let forward = forwards.find((f) => f.id === element.id);
      if (!forward) {
        forward = {
          id: element.id,
          name: element.name,
          nodeType: EWFNodeType.Arrow,
          arrowType: EWFArrowType.Forward,
          fromNodeId: element.sourceRef.id,
          toNodeId: element.targetRef.id
        } as IWFForward;
        forwards.push(forward);
      } else {
        forward.name = element.name;
        forward.fromNodeId = element.sourceRef.id;
        forward.toNodeId = element.targetRef.id;
        forward.arrowType = EWFArrowType.Forward;
      }

      targetWorkflow.decisions = decisions.filter((d) => d.fromNodeId !== element.sourceRef.id);
    }
  }

  public syncLane(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any) {
    const targetWorkflow = this.resolveTargetWorkflow(element, workflow, currentTargetWorkflow);
    if (!targetWorkflow?.lanes) return;

    const assignment = {
      assignmentId: "Everyone",
      assignmentType: EWFAssignmentType.everyone,
      roleAssignmentParameter: undefined,
      dynamicAssignmentAction: undefined
    } as IWFNodeAssignment;

    let lane = targetWorkflow.lanes.find((l) => l.id === element.id);
    if (!lane) {
      lane = {
        id: element.id,
        name: element.name,
        nodeType: EWFNodeType.Lane,
        assignment
      } as IWFLane;
      targetWorkflow.lanes.push(lane);
    } else {
      lane.name = element.name;
      if (!lane.assignment) {
        lane.assignment = assignment;
      }
    }
  }

  private findParentSubprocess(element: any): any | undefined {
    if (!element || !element.parent) return undefined;
    if (element.parent.$type === "bpmn:SubProcess") return element.parent;
    return this.findParentSubprocess(element.parent);
  }

  /**
   * Convenience method for creating elements
   */
  createElement(element: ModdleElement, workflow: IWorkFlow): void {
    this.syncElement(element, workflow);
  }

  /**
   * Convenience method for updating elements
   */
  updateElement(element: ModdleElement, workflow: IWorkFlow): void {
    this.syncElement(element, workflow);
  }

  /**
   * Convenience method for deleting elements
   */
  deleteElement(element: ModdleElement, workflow: IWorkFlow, currentTargetWorkflow?: any): void {
    // For deletion, we need to remove the element from the workflow model
    if (!element || !workflow) return;

    const businessObject = element.businessObject;
    if (!businessObject) return;

    console.log(`[WorkflowSync] Deleting element ${element.id} (${businessObject.$type})`);

    const targetWorkflow = currentTargetWorkflow || this.findTargetWorkflowInHierarchy(businessObject, workflow);
    if (!targetWorkflow) {
      console.log(`[WorkflowSync] No target workflow found for element ${element.id}`);
      return;
    }

    console.log(`[WorkflowSync] Target workflow found for element ${element.id}`);

    switch (businessObject.$type) {
      case "bpmn:Task":
      case "bpmn:UserTask":
      case "bpmn:SendTask":
      case "bpmn:SubProcess":
      case "bpmn:CallActivity":
        targetWorkflow.steps = targetWorkflow.steps?.filter((s) => s.id !== element.id);
        break;
      case "bpmn:ExclusiveGateway":
      case "bpmn:ParallelGateway":
      case "bpmn:InclusiveGateway":
        targetWorkflow.gateways = targetWorkflow.gateways?.filter((g) => g.id !== element.id);
        break;
      case "bpmn:StartEvent":
      case "bpmn:EndEvent":
        targetWorkflow.events = targetWorkflow.events?.filter((e) => e.id !== element.id);
        break;
      case "bpmn:BoundaryEvent":
        // Remove from events array if exists
        targetWorkflow.events = targetWorkflow.events?.filter((e) => e.id !== element.id);

        if (targetWorkflow.steps) {
          targetWorkflow.steps.forEach((step) => {
            if (step.boundaryEvent?.id === element.id) {
              step.boundaryEvent = undefined;
              console.log(`[WorkflowSync] Removed boundary event ${element.id} from step ${step.id}`);
            }
          });
        }
        break;
      case "bpmn:IntermediateCatchEvent":
        targetWorkflow.events = targetWorkflow.events?.filter((e) => e.id !== element.id);
        break;
      case "bpmn:SequenceFlow":
        targetWorkflow.forwards = targetWorkflow.forwards?.filter((f) => f.id !== element.id);
        targetWorkflow.decisions = targetWorkflow.decisions?.filter((d) => d.id !== element.id);
        break;
      case "bpmn:Lane":
        targetWorkflow.lanes = targetWorkflow.lanes?.filter((l) => l.id !== element.id);
        break;
      default:
        console.log(`[WorkflowSync] Unknown element type for deletion: ${businessObject.$type}`);
        break;
    }
  }

  /**
   * Resolves the target workflow for an element, handling both root and subprocess contexts
   */
  private resolveTargetWorkflow(element: any, workflow: IWorkFlow, currentTargetWorkflow?: any): any {
    // If we have a currentTargetWorkflow (from provider), use it
    if (currentTargetWorkflow) {
      return currentTargetWorkflow;
    }

    // Fallback: use the original logic for backward compatibility
    const parentSubprocess = this.findParentSubprocess(element);
    return parentSubprocess
      ? workflow.nodes?.steps?.find((s) => s.id === parentSubprocess.id)?.subprocess
      : workflow.nodes;
  }

  /**
   * Traverses the element hierarchy to find the correct target workflow
   * Handles nested subprocesses by walking up the parent chain
   */
  private findTargetWorkflowInHierarchy(element: any, workflow: IWorkFlow): any {
    if (!element || !workflow || !workflow.nodes) {
      console.log(`[WorkflowSync] No element, workflow, or workflow.nodes found for element ${element?.id}`);
      return workflow?.nodes;
    }

    // Start with the root workflow
    let currentWorkflow = workflow.nodes;
    let currentElement = element;
    let hierarchyPath = [element.id];

    console.log(`[WorkflowSync] Starting hierarchy traversal for element ${element.id} (${element.$type})`);

    // Traverse up the parent chain
    while (currentElement && currentElement.parent) {
      const parent = currentElement.parent;
      hierarchyPath.push(parent.id);

      // If parent is a subprocess, we need to find it in the current workflow
      if (parent.$type === "bpmn:SubProcess") {
        console.log(`[WorkflowSync] Found subprocess parent ${parent.id} for element ${element.id}`);

        // Look for the subprocess step in the current workflow
        const subprocessStep = currentWorkflow.steps?.find((s) => s.id === parent.id);

        if (subprocessStep && subprocessStep.subprocess) {
          console.log(`[WorkflowSync] Moving to subprocess workflow for ${parent.id}`);
          // Move to the subprocess workflow
          currentWorkflow = subprocessStep.subprocess;
          currentElement = parent;
        } else {
          console.log(
            `[WorkflowSync] Subprocess step ${parent.id} not found in current workflow, staying at current level`
          );
          // Subprocess step doesn't exist, this means we're at the root level
          // or there's an inconsistency - return the current workflow
          break;
        }
      } else {
        console.log(`[WorkflowSync] Parent ${parent.id} is not a subprocess (${parent.$type}), moving up`);
        // Parent is not a subprocess, move up the chain
        currentElement = parent;
      }
    }

    console.log(`[WorkflowSync] Final target workflow for element ${element.id}: ${hierarchyPath.join(" -> ")}`);
    return currentWorkflow;
  }
}
